/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { task } from 'ember-concurrency';
import DomainModel, { DnsDomainObject } from 'cio/models/domain';
import DomainVerificationService from 'cio/services/domain-verification';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';

export enum DomainVerificationSection {
  Mx = 'mx',
  Ownership = 'ownership',
  Spf = 'spf',
  Dkim = 'dkim',
  Dmarc = 'dmarc',
}

interface DomainVerifyAuthenticationArgs {
  domain: DomainModel;
  onVerify?: () => unknown;
  onClose: () => unknown;
}

function filterRecordsByName(type: string) {
  return computed('dnsRecords.[]', function (this: DomainVerifyAuthentication) {
    return this.dnsRecords.filter((record) => type === record.name);
  });
}

const DMARC_EXPLANATIONS = {
  notChecked: 'Verify the domain to check for an existing DMARC policy.',
  unverifiedEmpty: htmlSafe(
    'No DMARC policy detected. Add the TXT record below to meet the minimum policy required for verification or <a href="https://dmarc.org/overview/" target="_blank" rel="noopener noreferrer">learn more about implementing stricter policies</a> like "reject" and "quarantine".',
  ),
  unverified: (value: string) =>
    htmlSafe(
      `Invalid DMARC policy detected: <code>${value}</code>. Add the TXT record below to meet the minimum policy required for verification or <a href="https://dmarc.org/overview/" target="_blank" rel="noopener noreferrer">learn more about implementing stricter policies</a> like "reject" and "quarantine".`,
    ),
  verified: 'The following TXT record was found when last verified:',
};

export default class DomainVerifyAuthentication extends Component<DomainVerifyAuthenticationArgs> {
  @service domainVerification!: DomainVerificationService;
  @tracked expandedSections: DomainVerificationSection[] = [];
  @tracked errors: string[] = [];

  // While we have an intermediate page to migrate to Mailgun, we're switching between "transition"
  // properties and normal properties (verifiedDomain, verifiedSpf, etc.). When it is done, we can get
  // rid of this entirely and simply use the primary properties from the domain model.
  @computed('args.domain.dnsRecords.[]')
  get dnsRecords() {
    return this.args.domain.dnsRecords;
  }

  @filterRecordsByName('Domain') mxRecords!: DnsDomainObject[];
  @filterRecordsByName('Ownership') ownershipRecords!: DnsDomainObject[];
  @filterRecordsByName('SPF') spfRecords!: DnsDomainObject[];
  @filterRecordsByName('DKIM') dkimRecords!: DnsDomainObject[];
  @filterRecordsByName('DMARC') _dmarcRecords!: DnsDomainObject[];

  dmarcRecordSuggestion = [
    {
      type: 'TXT',
      domain: '_dmarc',
      value: 'v=DMARC1; p=none',
      name: 'DMARC',
    },
  ];

  get dmarcRecords() {
    let { verifiedDmarc, _dmarcRecords } = this;

    if (verifiedDmarc == null) {
      return [];
    }

    if (verifiedDmarc === false) {
      return this.dmarcRecordSuggestion;
    }

    return _dmarcRecords;
  }

  get verifiedDomain() {
    return this.args.domain.verifiedDomain;
  }

  get verifiedSpf() {
    return this.args.domain.verifiedSpf;
  }

  get verifiedDkim() {
    return this.args.domain.verifiedDkim;
  }

  get verifiedDmarc() {
    return this.args.domain.verifiedDmarc;
  }

  get dmarcExplanation() {
    let { verifiedDmarc, _dmarcRecords } = this;

    if (verifiedDmarc === true) {
      return DMARC_EXPLANATIONS.verified;
    }

    if (verifiedDmarc === false) {
      if (_dmarcRecords.length === 0) {
        return DMARC_EXPLANATIONS.unverifiedEmpty;
      }

      let [firstRecord] = _dmarcRecords;

      return DMARC_EXPLANATIONS.unverified(firstRecord.value);
    }

    return DMARC_EXPLANATIONS.notChecked;
  }

  constructor(owner: unknown, args: DomainVerifyAuthenticationArgs) {
    super(owner, args);
    this.setDefaultExpandedSections();
  }

  @computed('verifyTask.{performCount,isRunning}')
  get hasCheckedVerification() {
    // @ts-ignore task
    return this.verifyTask.performCount > 0 && !this.verifyTask.isRunning;
  }

  verifyTask = task(async () => {
    try {
      // @ts-ignore
      await this.domainVerification.verifyTask.perform(this.args.domain, 'verify_authentication');
      this.setDefaultExpandedSections();

      if (this.args.onVerify) {
        this.args.onVerify();
      }
    } catch (errors) {
      this.errors = errors;
    }
  });

  @action
  toggleSection(name: DomainVerificationSection) {
    if (this.expandedSections.includes(name)) {
      this.expandedSections = this.expandedSections.filter((n) => n !== name);
    } else {
      this.expandedSections = [...this.expandedSections, name];
    }
  }

  private setDefaultExpandedSections() {
    let sections: DomainVerificationSection[] = [];

    if (!this.verifiedDomain) {
      sections.push(DomainVerificationSection.Mx);
    }

    if (!this.verifiedSpf) {
      sections.push(DomainVerificationSection.Spf);
    }

    if (!this.verifiedDkim) {
      sections.push(DomainVerificationSection.Dkim);
    }

    if (!this.verifiedDmarc) {
      sections.push(DomainVerificationSection.Dmarc);
    }

    this.expandedSections = sections;
  }
}
